import type { CCFeedMove, CCFeedVenue } from "../api/types";
import { groupThousands } from "./money";
import { priceText } from "./cex";
import { formatAge } from "./format";

/**
 * Правила показа сводки фида crypto-console.
 *
 * Отдельным модулем по той же причине, что lib/cex.ts: здесь живут ПРАВИЛА
 * ПУСТОТЫ и устаревания, а не вёрстка. У строки биржи три разных ответа —
 * «значение свежее», «значение есть, но старше порога сервера» и «значения
 * нет», — и в разметке они слились бы в одинаковое число или одинаковый
 * прочерк. Проверяются они тестом, а не глазами на экране.
 */

/** Разбор числовой строки. null — величины нет: пустое поле и есть «не знаем». */
export function num(value: string | null | undefined): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Число с разрядами и фиксированными знаками; прочерк, если величины нет. */
export function fixedText(value: string | null | undefined, digits: number): string {
  const n = num(value);
  return n === null ? "—" : groupThousands(n.toFixed(digits));
}

/**
 * Число со знаком. Плюс явный: дельта и разложение движения читаются по
 * знаку, и «12.3» рядом с «−4.1» глаз принимает за модуль, а не за направление.
 */
export function signedText(value: string | null | undefined, digits: number): string {
  const n = num(value);
  if (n === null) return "—";
  const body = groupThousands(Math.abs(n).toFixed(digits));
  // Ноль после округления — без знака: «+0.00» обещает направление, которого нет.
  if (Number(Math.abs(n).toFixed(digits)) === 0) return body;
  return n > 0 ? `+${body}` : `-${body}`;
}

/** Цена актива: один знак после запятой, разряды — как на панели плотности. */
export const price = priceText;

/** Объём в BTC. Три знака: минутный поток бывает в десятые доли монеты. */
export function btcText(value: string | null | undefined): string {
  return fixedText(value, 3);
}

/** Доллары целыми с разрядами: оборот окна — десятки миллионов, центы шумят. */
export function usdText(value: string | null | undefined): string {
  const n = num(value);
  return n === null ? "—" : `$${groupThousands(Math.round(n).toFixed(0))}`;
}

/** Базисные пункты, два знака. */
export function bpText(value: string | null | undefined): string {
  const n = num(value);
  return n === null ? "—" : `${n.toFixed(2)} б.п.`;
}

/** Окно потока: 60 → «1 мин». Незнакомое — секундами, а не выдуманным именем. */
export function spanLabel(spanS: number): string {
  if (spanS > 0 && spanS % 60 === 0) return `${spanS / 60} мин`;
  return `${spanS} с`;
}

/** Окно разложения движения: «10s» → «10 с», «5m» → «5 мин». */
export function moveWindowLabel(w: string): string {
  const m = /^(\d+)(s|m|h)$/.exec(w);
  if (!m) return w;
  const unit = m[2] === "s" ? "с" : m[2] === "m" ? "мин" : "ч";
  return `${m[1]} ${unit}`;
}

/**
 * Ячейка с тремя состояниями.
 *
 * `stale` — значение ЕСТЬ, но сервер пометил его старым: показываем серым, а
 * не прячем, потому что ставка финансирования «стареет» за десять секунд, а
 * меняется раз в час — прочерк на её месте врал бы сильнее, чем старое число.
 */
export interface Cell {
  text: string;
  stale: boolean;
  title: string | undefined;
}

export function cell(text: string | null, why: string | undefined): Cell {
  const reason = why || undefined;
  if (text === null) return { text: "—", stale: false, title: reason };
  return { text, stale: Boolean(reason), title: reason };
}

/** Вершина биржи: bid / ask. Нет любой стороны — прочерк с причиной. */
export function topCell(v: CCFeedVenue): Cell {
  if (num(v.bid) === null || num(v.ask) === null) {
    return { text: "—", stale: false, title: v.top_why || "вершины нет" };
  }
  return { text: `${price(v.bid)} / ${price(v.ask)}`, stale: false, title: undefined };
}

export function markCell(v: CCFeedVenue): Cell {
  return cell(num(v.mark) === null ? null : price(v.mark), v.mark_why);
}

/** Интервал финансирования: 3600 → «/1ч», 28800 → «/8ч». */
export function fundingInterval(s: number | undefined): string {
  if (!s || s <= 0) return "";
  if (s % 3600 === 0) return `/${s / 3600}ч`;
  if (s % 60 === 0) return `/${s / 60}мин`;
  return `/${s}с`;
}

/**
 * Ставка финансирования по единице, которую ЗАЯВИЛА биржа.
 *
 * `undeclared` нельзя читать долей: 0.01 долей — это 1 %, а 0.01 процента —
 * в сто раз меньше, и угадать за биржу значило бы показать одно из двух чисел
 * неверным в сто раз. Поэтому сырое число и подсказка вместо пересчёта.
 */
export function fundingCell(v: CCFeedVenue): Cell {
  const n = num(v.funding_rate);
  if (n === null) return cell(null, v.funding_why);
  const per = fundingInterval(v.funding_interval_s);
  let text: string;
  let unitNote: string | undefined;
  switch (v.funding_unit) {
    case "fraction":
      text = `${(n * 100).toFixed(4)}%${per}`;
      break;
    case "percent":
      text = `${n.toFixed(4)}%${per}`;
      break;
    default:
      text = `${v.funding_rate}${per}`;
      unitNote = "единица не подтверждена венью";
  }
  const c = cell(text, v.funding_why);
  return { ...c, title: joinTitles(unitNote, c.title) };
}

/**
 * Открытый интерес. `unconfirmed` — сырое число: у части бирж это контракты,
 * у части — монеты, и подпись «BTC» к нему была бы догадкой. `base_observed` —
 * монеты по записанному наблюдению, а не по документации венью: число то же,
 * что у `base`, но основание другое, и подсказка это называет.
 */
export function oiCell(v: CCFeedVenue): Cell {
  if (num(v.oi) === null) return cell(null, v.oi_why);
  let text: string;
  let unitNote: string | undefined;
  switch (v.oi_unit) {
    case "base":
      text = `${fixedText(v.oi, 1)} BTC`;
      break;
    case "base_observed":
      text = `${fixedText(v.oi, 1)} BTC`;
      unitNote = "единица — по наблюдению, не по документации венью";
      break;
    case "quote":
      text = usdText(v.oi);
      break;
    default:
      text = fixedText(v.oi, 1);
      unitNote = "единица не подтверждена венью";
  }
  const c = cell(text, v.oi_why);
  return { ...c, title: joinTitles(unitNote, c.title) };
}

/** Единица открытого интереса словом — для списка изменений OI. */
export function oiUnitLabel(unit: string): string {
  return unit === "base" || unit === "base_observed" ? "BTC" : unit === "quote" ? "$" : "";
}

function joinTitles(...parts: (string | undefined)[]): string | undefined {
  const s = parts.filter(Boolean).join("\n");
  return s || undefined;
}

/**
 * Спред сводной вершины. Отрицательный — перекрёст, и подпись говорит, откуда
 * он обычно берётся: набор сводит USD- и USDT-перпы по допущению паритета, и
 * «перекрёст» на нём — расхождение валют котировки, а не арбитраж.
 */
export const CROSSED_TITLE =
  "скорее всего разные котируемые валюты (USD/USDT): crypto-console сводит их по допущению паритета";

export function spreadLabel(spreadBp: string | undefined): { text: string; crossed: boolean } {
  const n = num(spreadBp);
  if (n === null) return { text: "—", crossed: false };
  if (n < 0) return { text: `перекрёст ${Math.abs(n).toFixed(2)} б.п.`, crossed: true };
  return { text: `спред ${n.toFixed(2)} б.п.`, crossed: false };
}

/** Сумма двух частей движения. Нет обеих — неизвестно; одна — она и есть. */
export function sumParts(a: string | undefined, b: string | undefined): string | undefined {
  const x = num(a);
  const y = num(b);
  if (x === null && y === null) return undefined;
  return String((x ?? 0) + (y ?? 0));
}

/** Строка разложения движения, уже собранная в четыре слагаемых. */
export interface MoveRow {
  window: string;
  mid: string;
  takers: string;
  takersTitle: string;
  removal: string;
  removalTitle: string;
  addition: string;
  additionTitle: string;
  undefinedPart: string;
  /** Разложения нет — причина; строка тогда целиком в прочерках. */
  why: string | undefined;
  /** Разложение неполное — причина. */
  partial: string | undefined;
}

export function moveRow(m: CCFeedMove): MoveRow {
  const d = 2;
  return {
    window: moveWindowLabel(m.window),
    mid: signedText(m.why ? undefined : m.mid_change, d),
    takers: signedText(sumParts(m.takers_buy, m.takers_sell), d),
    takersTitle: `покупки ${signedText(m.takers_buy, d)} · продажи ${signedText(m.takers_sell, d)}`,
    removal: signedText(sumParts(m.removal_bids, m.removal_asks), d),
    removalTitle: `биды ${signedText(m.removal_bids, d)} · аски ${signedText(m.removal_asks, d)}`,
    addition: signedText(sumParts(m.addition_bids, m.addition_asks), d),
    additionTitle: `биды ${signedText(m.addition_bids, d)} · аски ${signedText(m.addition_asks, d)}`,
    undefinedPart: signedText(m.undefined, d),
    why: m.why || undefined,
    partial:
      m.confidence && m.confidence !== "complete"
        ? joinTitles(`разложение: ${m.confidence}`, m.reason)
        : undefined,
  };
}

/**
 * Возраст сводки. Порог пять секунд: трейдер кладёт сводку раз в секунду, и
 * пять пропущенных тактов — уже не задержка опроса, а вставший источник.
 */
export const STALE_AFTER_MS = 5000;

export function summaryAgeMs(at: string, now: number): number {
  const t = Date.parse(at);
  return Number.isNaN(t) ? Number.NaN : Math.max(0, now - t);
}

/**
 * Координаты спарклайна цены. Масштаб — по собственному размаху ряда: на
 * пятнадцати минутах BTC проходит десятки долларов из восьмидесяти тысяч, и от
 * нуля линия была бы прямой.
 */
export function pricePath(
  mids: (number | null)[],
  width: number,
  height: number,
): { d: string; min: number; max: number } | null {
  const vals = mids.filter((v): v is number => v !== null);
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const parts: string[] = [];
  let pen = false;
  mids.forEach((v, i) => {
    if (v === null) {
      // Пробел рвёт линию: соединённый, он рисовал бы плавный ход там, где
      // данных не было вовсе.
      pen = false;
      return;
    }
    const x = (i / (mids.length - 1)) * width;
    const y = height - ((v - min) / span) * height;
    parts.push(`${pen ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`);
    pen = true;
  });
  return { d: parts.join(" "), min, max };
}

// venueAge — возраст вершины биржи. До секунды — в миллисекундах: вершины
// живут сотни миллисекунд, и «0 с» у всех строк прятало бы разницу между
// 50 мс и 900 мс, ради которой колонка и есть. Без вершины — прочерк.
export function venueAge(v: { bid?: string; ask?: string; age_ms: number }): string {
  if (v.bid === undefined && v.ask === undefined) return "—";
  if (v.age_ms < 1000) return `${Math.max(0, Math.round(v.age_ms))} мс`;
  return formatAge(v.age_ms);
}
